import styled from 'styled-components';
import { Box, Link, mq, Text, useMq, useThemeColor } from '@wistia/vhs';
import { externalLinks } from '~/externalLinks';
import { AnonymousHeader } from '~/components/Header/AnonymousHeader';
import { NameOrLogo } from '~/components/Header/AccountLogoLink';
import { trackStructEvent } from '~/utilities/snowplow';
import type { JSX } from 'react';
import { useUnauthorizedMediaQuery } from './__gql/UnauthorizedMedia.gql.gen';
import { Background, StyledPaper, StyledHeader } from '../shared/components';

const ContentContainer = styled(StyledPaper)`
  padding: 40px;
  padding-bottom: 24px;

  ${mq.smAndDown} {
    padding-bottom: 18px;
  }
`;

const TextContainer = styled.div`
  padding: 0 44px;

  ${mq.smAndDown} {
    padding: 0;
  }
`;

const LinkText = styled(Text)`
  margin-top: 24px;

  ${mq.smAndDown} {
    margin-right: -16px;
    margin-top: 44px;
  }
`;

const UnauthorizedMediaScreen = (): JSX.Element => {
  const brandBlue500 = useThemeColor('brandBlue500');
  const { data } = useUnauthorizedMediaQuery();
  const { isMdAndUp } = useMq();

  return (
    <>
      {/* @ts-expect-error improperly inferred prop */}
      <AnonymousHeader />
      <Background
        withBackgroundImage={false}
        withGradient={true}
      >
        <Box
          direction="column"
          alignItems="end"
        >
          <ContentContainer>
            {isMdAndUp ? (
              <StyledHeader style={{ marginBottom: '24px' }}>
                <NameOrLogo
                  account={data?.currentAccount}
                  logoMaxHeight={100}
                />
              </StyledHeader>
            ) : null}

            <TextContainer>
              <StyledHeader
                variant="headline1"
                align="center"
              >
                Whoops! You don&apos;t have access to this media.
              </StyledHeader>
              <Text
                variant="body2"
                style={{ marginBottom: '16px' }}
              >
                Please contact the person who sent you this link for help adjusting the permissions.
              </Text>
              <Text variant="body2">
                If you have a Wistia account, you can also log in to check your permissions.
              </Text>
            </TextContainer>
            <Link
              beforeAction={() => {
                trackStructEvent({
                  category: 'button',
                  action: 'click',
                  label: 'unauthorized-media-demo',
                });
              }}
              href={externalLinks.about}
              underlineOnHover={true}
            >
              <LinkText
                variant="caption"
                colorOverride={brandBlue500}
                align="right"
              >
                Learn how to host your own videos
              </LinkText>
            </Link>
          </ContentContainer>
        </Box>
      </Background>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default UnauthorizedMediaScreen;
